/* My Easy Bites — theme.js (global, deferred, no dependencies) */
(() => {
  const MEB = (window.MEB = window.MEB || {});

  /* ---------- Money ---------- */
  MEB.formatMoney = (cents) => {
    const fmt = MEB.moneyFormat || '${{amount}}';
    const n = Number(cents) / 100;
    const withDelims = (num, dec, thou, sep) => {
      const [i, f] = num.toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (f ? sep + f : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(n, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(n, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(n, 0, '.', ',');
        default: return withDelims(n, 2, ',', '.');
      }
    });
  };

  /* ---------- Analytics (Meta Pixel / GA4 / dataLayer) ----------
     Fires only if the tags are present on the page. Shopify's own Web Pixels
     (Facebook & Instagram app, Google & YouTube app) already send these events —
     disable "Theme event tracking" in theme settings if you use those apps,
     to avoid double counting. */
  const TRACKING_ON = MEB.track === true;
  const GA_MAP = { ViewContent: 'view_item', AddToCart: 'add_to_cart', InitiateCheckout: 'begin_checkout' };
  MEB.track = (name, d = {}) => {
    if (!TRACKING_ON) return;
    const value = d.price ? d.price / 100 : undefined;
    const eventID = `${name}-${d.variantId || ''}-${Date.now()}`;
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('track', name, { content_ids: [String(d.variantId)], content_type: 'product', content_name: d.title, value, currency: MEB.currency }, { eventID });
      }
    } catch (e) {}
    const ecommerce = { currency: MEB.currency, value, items: [{ item_id: String(d.variantId), item_name: d.title, price: value, quantity: d.quantity || 1 }] };
    try { if (typeof window.gtag === 'function') window.gtag('event', GA_MAP[name], ecommerce); } catch (e) {}
    (window.dataLayer = window.dataLayer || []).push({ ecommerce: null });
    window.dataLayer.push({ event: GA_MAP[name], meb_event_id: eventID, ecommerce });
  };

  /* ---------- Smooth anchors (stars → reviews etc.) ---------- */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a || a.getAttribute('href').length < 2) return;
    const target = document.querySelector(a.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    const header = document.querySelector('.site-header');
    const offset = header ? header.offsetHeight + 12 : 12;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: target.getBoundingClientRect().top + scrollY - offset, behavior: reduce ? 'auto' : 'smooth' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  /* ---------- Announcement bar: rotation + evergreen countdown ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.msgs = [...this.querySelectorAll('.announcement__msg')];
      this.i = 0;
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.msgs.length > 1 && !reduce) {
        this.start();
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());
      }
      this.querySelectorAll('[data-countdown]').forEach((el) => this.countdown(el));
    }
    start() { this.stop(); this.timer = setInterval(() => this.next(), (+this.dataset.speed || 5) * 1000); }
    stop() { clearInterval(this.timer); }
    next() {
      this.msgs[this.i].classList.remove('is-active');
      this.msgs[this.i].setAttribute('aria-hidden', 'true');
      this.i = (this.i + 1) % this.msgs.length;
      this.msgs[this.i].classList.add('is-active');
      this.msgs[this.i].removeAttribute('aria-hidden');
    }
    countdown(el) {
      const hour = +el.dataset.resetHour || 0;
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const now = new Date();
        const end = new Date(now);
        end.setHours(hour, 0, 0, 0);
        if (end <= now) end.setDate(end.getDate() + 1);
        let s = Math.floor((end - now) / 1000);
        const h = Math.floor(s / 3600); s -= h * 3600;
        const m = Math.floor(s / 60); s -= m * 60;
        el.querySelector('[data-h]').textContent = pad(h);
        el.querySelector('[data-m]').textContent = pad(m);
        el.querySelector('[data-s]').textContent = pad(s);
      };
      tick();
      setInterval(tick, 1000);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Modal helper for <dialog> drawers/popups ---------- */
  MEB.openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      dialog._opener && dialog._opener.focus && dialog._opener.focus();
    }, { once: true });
  };
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-dialog]');
    if (opener) {
      const d = document.getElementById(opener.dataset.openDialog);
      if (d) { e.preventDefault(); MEB.openDialog(d, opener); }
      return;
    }
    const closer = e.target.closest('[data-close-dialog]');
    if (closer) { closer.closest('dialog').close(); return; }
    // backdrop click
    if (e.target.tagName === 'DIALOG' && e.target.open) e.target.close();
  });

  /* ---------- Cart drawer ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-remove-line]');
        if (rm) { e.preventDefault(); this.change(rm.dataset.removeLine, 0); }
      });
      this.addEventListener('submit', (e) => {
        const f = e.target.closest('[data-upsell-form]');
        if (!f) return;
        e.preventDefault();
        MEB.addToCart(new FormData(f)).then(() => this.refresh(true));
      });
    }
    open() { MEB.openDialog(this.dialog); }
    change(line, quantity) {
      return fetch(MEB.routes.cartChange + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: +line, quantity })
      }).then(() => this.refresh(false));
    }
    refresh(openAfter) {
      return fetch(`${MEB.routes.root.replace(/\/$/, '')}/?section_id=cart-drawer`)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('[data-cart-drawer-content]');
          const current = this.querySelector('[data-cart-drawer-content]');
          if (fresh && current) current.replaceWith(fresh);
          MEB.updateCartCount();
          if (openAfter) this.open();
        });
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  MEB.addToCart = (formData) =>
    fetch(MEB.routes.cartAdd + '.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData })
      .then((r) => r.json().then((j) => (r.ok ? j : Promise.reject(j))));

  MEB.updateCartCount = () =>
    fetch(MEB.routes.cart + '.js').then((r) => r.json()).then((c) => {
      document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = c.item_count || ''; el.dataset.count = c.item_count; });
    });

  /* ---------- Product form (used on product page, cards, final CTA, bundles) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.idInput = this.form.querySelector('input[type=hidden][name=id]');
      this.button = this.form.querySelector('[type=submit]');
      this.error = this.querySelector('.product-form__error');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.form.addEventListener('change', (e) => {
        const r = e.target;
        if (r.type === 'radio' && (r.name === 'id' || r.name.startsWith('variant-choice'))) {
          if (this.idInput && r.name !== 'id') this.idInput.value = r.value;
          this.setLabel(r.dataset.price);
          this.emit(r.value, r.dataset.price, r.dataset.compare);
        }
      });
      if (this.hasAttribute('data-sync')) {
        document.addEventListener('meb:variant', (e) => {
          if (e.detail.source === this) return;
          if (this.idInput) this.idInput.value = e.detail.id;
          const radio = this.form.querySelector(`input[type=radio][value="${e.detail.id}"]`);
          if (radio) radio.checked = true;
          this.setLabel(e.detail.price);
        });
      }
    }
    current() {
      const r = this.form.querySelector('input[type=radio][name=id]:checked');
      if (r) return { id: r.value, price: +r.dataset.price };
      return { id: this.idInput && this.idInput.value, price: +(this.idInput && this.idInput.dataset.price) };
    }
    setLabel(price) {
      if (price == null || !this.button) return;
      const tpl = this.dataset.labelTemplate || '';
      const label = this.button.querySelector('[data-cta-label]');
      if (label && tpl) label.textContent = tpl.replace('[price]', MEB.formatMoney(price));
      if (this.idInput) this.idInput.dataset.price = price;
    }
    emit(id, price, compare) {
      document.dispatchEvent(new CustomEvent('meb:variant', { detail: { id, price: +price, compare: +compare, source: this } }));
    }
    onSubmit(e) {
      const cur = this.current();
      const qty = +(this.form.querySelector('[name=quantity]') || {}).value || 1;
      const data = { variantId: cur.id, price: cur.price * qty, title: this.dataset.productTitle, quantity: qty };
      MEB.track('AddToCart', data);
      if (this.dataset.mode !== 'drawer') {
        // Direct checkout: native POST with return_to=/checkout. Works without JS.
        MEB.track('InitiateCheckout', data);
        this.button && this.button.classList.add('is-loading');
        return;
      }
      e.preventDefault();
      this.button && this.button.classList.add('is-loading');
      if (this.error) this.error.hidden = true;
      MEB.addToCart(new FormData(this.form))
        .then(() => {
          const drawer = document.querySelector('cart-drawer');
          if (drawer) return drawer.refresh(true);
          window.location.href = MEB.routes.cart;
        })
        .catch((err) => {
          if (this.error) { this.error.textContent = (err && (err.description || err.message)) || MEB.strings.addError; this.error.hidden = false; }
        })
        .finally(() => this.button && this.button.classList.remove('is-loading'));
    }
  }
  customElements.define('product-form', ProductForm);

  // Restore button state on bfcache back-navigation from checkout
  window.addEventListener('pageshow', () => document.querySelectorAll('.is-loading').forEach((b) => b.classList.remove('is-loading')));
})();
